import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Slide,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  MoreVertRounded,
  ReceiptRounded,
  DriveFileRenameOutlineRounded,
  DeleteRounded,
  ContentCopyRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { useDialog } from "../../DialogContext";
import { backend_api } from "../../../static/server";
import ServiceTimeForm from "../Activities/ActivitiesForms/ServiceTimeForm";
import ExpenseForm from "../Activities/ActivitiesForms/ExpenseForm";
import BillGenerateForm from "../Billing/BillGenrateForm";
import {
  formattedDate,
  generateUniqueMatterName,
} from "../../../styles/styles";
import { DataGridNoData } from "../../../static/staticdata";
import DataTable from "../../DataTable";
import NoActivity from "../../../assets/no-activity.webp";
import axios from "axios";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";
import { useAccess } from "../../Auth/AccessProvider";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ActivityTableForDash = ({
  // Required props
  activities = [],
  loading = false,

  // Optional props with defaults
  height = "100%",
  minHeight = 0,
  emptyStateText = "No activities found",
  emptyStateImage = NoActivity,

  // Pagination props
  pagination = null,
  onPageChange = () => {},

  // Column visibility control
  hiddenColumns = [],
  showColumns = null,

  // Customization
  showActionColumn = true,
  showFilterChips = false,
  filterChips = [],
  showToolbar = true,
  CustomToolbar = null,
  extraBtn = null,

  // Callbacks
  onActivityDelete = null,
  onActivityEdit = null,
  onActivityUpdate = null,
  onRefresh = () => {},
  onBulkBill = null,

  // External dependencies
  token = "",
  teamMemberLawyerId = "",

  // Custom row actions
  customRowActions = null,

  // Edit functionality
  enableEdit = true,
  enableQuickBill = true,
  enableDuplicate = true,
  enableDelete = true,

  // Bulk selection
  enableBulkSelection = false,
  selectedRows = [],
  onSelectionChange = () => {},

  // Styles
  sx = {},
  tableProps = {},
}) => {
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const { hasAccess } = useAccess();

  // State management
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    rowData: null,
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);

  // Activity operations
  const handleStatusChange = async (id, status) => {
    try {
      if (onActivityUpdate) {
        onActivityUpdate(id, status);
      } else {
        await axios.patch(
          `${backend_api}firm-case/expense/${id}/`,
          { status },
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Status updated successfully");
        onRefresh();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleEditActivity = (data) => {
    if (onActivityEdit) {
      onActivityEdit(data);
    } else {
      if (data.type === "time") {
        showDialog(
          <ServiceTimeForm entryData={data.full_data} editMode={true} />,
          {
            dialogTitle: "Edit Time Entry",
            onSuccess: onRefresh,
          }
        );
      } else {
        showDialog(<ExpenseForm entryData={data.full_data} editMode={true} />, {
          dialogTitle: "Edit Expense Entry",
          onSuccess: onRefresh,
        });
      }
    }
  };

  const handleDeleteActivity = async (activity) => {
    if (onActivityDelete) {
      onActivityDelete(activity);
      return;
    }

    try {
      const res = await axios.delete(
        `${backend_api}firm-case/expense/${activity.id}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res?.status === 204) {
        onRefresh();
        toast.success("Activity deleted successfully");
      }
    } catch (error) {
      toast.error("Failed to delete the activity. Please try again");
    }
  };

  const handleDuplicateActivity = (activity) => {
    if (activity.type === "time") {
      showDialog(
        <ServiceTimeForm
          duplicateData={activity.full_data}
          duplicateMode={true}
        />,
        {
          dialogTitle: "Duplicate Time Entry",
          onSuccess: onRefresh,
        }
      );
    } else {
      showDialog(
        <ExpenseForm duplicateData={activity.full_data} duplicateMode={true} />,
        {
          dialogTitle: "Duplicate Expense Entry",
          onSuccess: onRefresh,
        }
      );
    }
  };

  const handleQuickBill = (activity) => {
    if (activity.bill_genrated) {
      toast.info("Bill already generated");
    } else {
      showDialog(<BillGenerateForm entryData={activity.full_data} />, {
        dialogTitle: "Draft Bill",
        onSuccess: onRefresh,
      });
    }
  };

  const handleActionMenuOpen = (event, row) => {
    setActionMenu({
      anchorEl: event.currentTarget,
      rowData: row,
    });
  };

  const handleActionMenuClose = () => {
    setActionMenu({ anchorEl: null, rowData: null });
  };

  const handleOpenDeleteDialog = (row) => {
    setDeleteRow(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;

    setDeleteLoad(true);
    try {
      await handleDeleteActivity(deleteRow);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setDeleteLoad(false);
    }
  };

  const handleBulkBillClick = () => {
    if (onBulkBill) {
      onBulkBill(selectedRows);
    } else {
      // Default bulk bill implementation
      showDialog(<BillGenerateForm entryData={selectedRows} />, {
        dialogTitle: "Draft Bulk Bill",
        onSuccess: onRefresh,
      });
    }
  };

  // All possible columns definition - EXACT SAME AS ACTIVITIES PAGE
  const allColumns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => handleActionMenuOpen(e, params.row)}
              >
                <MoreVertRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={actionMenu.anchorEl}
              open={Boolean(actionMenu.anchorEl)}
              onClose={handleActionMenuClose}
              slotProps={{
                root: { sx: { ".MuiList-root": { padding: 0 } } },
              }}
            >
              {enableQuickBill &&
                actionMenu.rowData &&
                !actionMenu.rowData.non_billable &&
                !actionMenu.rowData.bill_genrated && (
                  <MenuItem
                    onClick={() => {
                      handleQuickBill(actionMenu.rowData);
                      handleActionMenuClose();
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ReceiptRounded
                      fontSize="small"
                      sx={{ fontSize: 18, color: "action.active" }}
                    />

                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Draft Bill
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              {actionMenu.rowData &&
                !actionMenu.rowData.bill_genrated &&
                hasAccess("access_activities_edit") && (
                  <MenuItem
                    id="access_activities_edit"
                    onClick={() => {
                      handleEditActivity(actionMenu.rowData);
                      handleActionMenuClose();
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <DriveFileRenameOutlineRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Edit
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              {enableDuplicate && actionMenu.rowData && (
                <MenuItem
                  onClick={() => {
                    handleDuplicateActivity(actionMenu.rowData);
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <ContentCopyRounded
                    fontSize="small"
                    sx={{ fontSize: 18, color: "action.active" }}
                  />

                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Duplicate
                    </Typography>
                  </Box>
                </MenuItem>
              )}
              {enableDelete &&
                actionMenu.rowData &&
                !actionMenu.rowData.bill_genrated &&
                hasAccess("access_activities_delete") && (
                  <MenuItem
                    id="access_activities_delete"
                    onClick={() => {
                      handleOpenDeleteDialog(actionMenu.rowData);
                      handleActionMenuClose();
                    }}
                    sx={{
                      borderTop: "1px solid #e5e5e5",
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      color: "error.main",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "error.contrastText",
                        "& .MuiSvgIcon-root": {
                          color: "error.contrastText",
                        },
                      },
                    }}
                  >
                    <DeleteRounded
                      sx={{
                        fontSize: 18,
                        color: "error.main",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Delete
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              {customRowActions &&
                customRowActions(actionMenu.rowData, {
                  closeMenu: () =>
                    setActionMenu({ anchorEl: null, rowData: null }),
                })}
            </Menu>
          </Box>
        ),
      },
      {
        field: "user",
        headerName: "User",
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.user}
          </Typography>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.type}
            size="small"
            color={params.row.type === "time" ? "primary" : "success"}
            variant="outlined"
          />
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 180,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              color: "text.primary",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.description}
          </Typography>
        ),
      },
      {
        field: "case_name",
        headerName: "Matter",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "text.primary",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              mb: 0.5,
              "&:hover": { cursor: "pointer", textDecoration: "underline" },
            }}
            title={params.row.client_ref}
            onClick={() => {
              navigate(
                `/dashboard/matter/${params.row.client_ref}/${params.row.case_id}/`
              );
            }}
          >
            Matter: {params.row.case_name}
          </Typography>
        ),
      },
      {
        field: "category_name",
        headerName: "Category",
        width: 150,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.category_name}
          </Typography>
        ),
      },
      {
        field: "quantity",
        headerName: "Quantity",
        width: 100,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.85rem", fontWeight: 500 }}
          >
            {params.row.quantity}
          </Typography>
        ),
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 90,
        renderCell: (params) => (
          <Chip
            label={params.row.currency}
            size="small"
            variant="outlined"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: "#f0f0f0",
            }}
          />
        ),
      },
      {
        field: "rate",
        headerName: "Rate",
        width: 100,
        renderCell: (params) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            >
              {params.row.currency_symbol}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "text.primary",
              }}
            >
              {params.row.rate}
            </Typography>
          </Box>
        ),
      },
      {
        field: "is_paid",
        headerName: "Payment",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.is_paid ? "Paid" : "Unpaid"}
            size="small"
            color={params.row.is_paid ? "success" : "error"}
            variant="outlined"
          />
        ),
      },
      {
        field: "bill_genrated",
        headerName: "Bill Status",
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.row.bill_genrated ? "Billed" : "Not Billed"}
            size="small"
            color={params.row.bill_genrated ? "success" : "warning"}
            variant="outlined"
          />
        ),
      },
      {
        field: "non_billable_amount",
        headerName: "Non-billable",
        width: 140,
        renderCell: (params) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            >
              {params.row.currency_symbol}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                fontWeight: params.row.non_billable_amount !== "-" ? 500 : 400,
                color:
                  params.row.non_billable_amount !== "-"
                    ? "error.main"
                    : "text.secondary",
              }}
            >
              {params.row.non_billable_amount}
            </Typography>
          </Box>
        ),
      },
      {
        field: "billable_amount",
        headerName: "Billable",
        width: 120,
        renderCell: (params) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            >
              {params.row.currency_symbol}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                fontWeight: params.row.billable_amount !== "-" ? 600 : 400,
                color:
                  params.row.billable_amount !== "-"
                    ? "success.main"
                    : "text.secondary",
              }}
            >
              {params.row.billable_amount}
            </Typography>
          </Box>
        ),
      },
      {
        field: "create_at",
        headerName: "Created At",
        width: 150,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.create_at}
          </Typography>
        ),
      },
    ],
    [
      actionMenu,
      token,
      navigate,
      showDialog,
      customRowActions,
      enableEdit,
      enableQuickBill,
      enableDuplicate,
      enableDelete,
    ]
  );

  // Filter columns based on props
  const filteredColumns = useMemo(() => {
    let columns = allColumns;

    // If showColumns is provided, only include those columns
    if (showColumns && Array.isArray(showColumns)) {
      columns = columns.filter((col) => showColumns.includes(col.field));
    }

    // Remove hidden columns
    if (hiddenColumns && Array.isArray(hiddenColumns)) {
      columns = columns.filter((col) => !hiddenColumns.includes(col.field));
    }

    // Remove action column if disabled
    if (!showActionColumn) {
      columns = columns.filter((col) => col.field !== "actions");
    }

    return columns;
  }, [allColumns, showColumns, hiddenColumns, showActionColumn]);

  // Transform activities data to rows - EXACT SAME FORMAT AS ACTIVITIES PAGE
  const rows = useMemo(() => {
    if (!Array.isArray(activities)) {
      console.error("activities is not an array:", activities);
      return [];
    }

    return activities.map((item) => {
      let duration = "-";
      if (item?.type === "time" && item?.time_in && item?.time_out) {
        const timeIn = new Date(item.time_in);
        const timeOut = new Date(item.time_out);
        const diffMs = timeOut - timeIn;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(
          (diffMs % (1000 * 60 * 60)) / (1000 * 60)
        );
        duration = `${diffHours}h ${diffMinutes}m`;
      }

      return {
        id: item?.id,
        user: item?.lawyer
          ? `${item.lawyer.first_name || ""} ${
              item.lawyer.last_name || ""
            }`.trim() ||
            item?.lawyer?.reminder_mail ||
            "-"
          : item?.created_by_emp
          ? `${item.created_by_emp.first_name || ""} ${
              item.created_by_emp.last_name || ""
            }`.trim() ||
            item?.created_by_emp?.reminder_mail ||
            "-"
          : "-",
        type: item?.type ?? "-",
        description: item?.description ?? "-",
        case_id: item?.case?.id ?? null,
        client_ref: item?.case?.client_ref ?? null,
        case_name: item?.case?.case_name
          ? generateUniqueMatterName(item.case.id, item.case.case_name)
          : "-",
        quantity: item?.type === "time" ? duration : item?.quantity ?? "-",
        currency: item?.currency ?? "$",
        currency_symbol: item?.currency
          ? getCurrencySymbol(item?.currency)
          : "$",
        rate: item?.hourly_rate ?? "-",
        non_billable_amount: item?.non_billable ? item?.total_payment : "-",
        billable_amount: item?.non_billable ? "-" : item?.total_payment,
        create_at: item?.created_at ? formattedDate(item.created_at) : "-",
        category_name: item?.category?.name ?? "-",
        is_paid: item?.is_paid ?? false,
        non_billable: item?.non_billable ?? false,
        bill_genrated: item?.bill_genrated ?? false,
        full_data: item,
      };
    });
  }, [activities]);

  // Custom no rows overlay
  const CustomNoRowsOverlay = useCallback(
    () => <DataGridNoData imgSrc={emptyStateImage} text={emptyStateText} />,
    [emptyStateImage, emptyStateText]
  );

  // Custom toolbar for bulk actions
  const DefaultCustomToolbar = useCallback(
    () => (
      <Box sx={{ display: "flex", gap: 1, p: 1, alignItems: "center" }}>
        {enableBulkSelection && selectedRows.length > 0 && (
          <Tooltip
            title={`Generate bill (${selectedRows.length} selected)`}
            arrow
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                onClick={handleBulkBillClick}
                disabled={selectedRows.length === 0}
                sx={{
                  border: "1px solid",
                  borderColor: "primary.light",
                  borderRadius: 1,
                }}
              >
                <ReceiptRounded fontSize="small" />
                <Typography
                  variant="caption"
                  sx={{ ml: 0.5, fontSize: "0.7rem" }}
                >
                  ({selectedRows.length})
                </Typography>
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    ),
    [enableBulkSelection, selectedRows.length, handleBulkBillClick]
  );

  const resolvedRowCount =
    pagination?.rowCount ?? pagination?.count ?? rows.length;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        minHeight,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <DataTable
        columns={filteredColumns}
        rows={rows}
        extraBtn={extraBtn}
        CustomNoRowsOverlay={CustomNoRowsOverlay}
        CustomToolbar={CustomToolbar || DefaultCustomToolbar}
        height="100%"
        minHeight={0}
        loading={loading}
        page={pagination?.page || 1}
        setPage={(page) => onPageChange(page)}
        pageSize={pagination?.pageSize || 10}
        rowCount={resolvedRowCount}
        count={resolvedRowCount}
        hasNextPage={pagination?.hasNextPage || false}
        hasPreviousPage={pagination?.hasPreviousPage || false}
        filterChips={showFilterChips ? filterChips : []}
        showToolbar={showToolbar}
        checkboxSelection={enableBulkSelection}
        onSelectionChange={onSelectionChange}
        disableSelectionOnClick={false}
        {...tableProps}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        TransitionComponent={Transition}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Delete Activity Confirmation
          </Typography>
          {deleteRow && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Are you sure you want to delete this {deleteRow.type} entry?
              {deleteRow.description && ` "${deleteRow.description}"`}
            </Typography>
          )}
        </DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={deleteLoad}
            size="small"
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: "capitalize" }}
          >
            Delete Activity
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(ActivityTableForDash);
